import { useEffect, useMemo, useRef, useState } from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getApiError } from "../api/errors";
import {
  commitCatalogSpreadsheet,
  inspectCatalogSpreadsheet,
  previewCatalogSpreadsheet,
} from "../api/storeImports";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";

const MAX_FILE_BYTES = 25 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [".xlsx", ".csv"];
const STEPS = ["Upload", "Map columns", "Preview", "Complete"];

const REQUIRED_FIELDS = [
  { key: "external_id", label: "External ID / SKU", helper: "Stable product identifier from the old POS." },
  { key: "name", label: "Product name", helper: "Customer-facing product description." },
  { key: "price", label: "Selling price", helper: "Current retail selling price in USD." },
  { key: "quantity", label: "Opening quantity", helper: "Physical stock snapshot at migration time." },
];

const OPTIONAL_FIELDS = [
  { key: "brand", label: "Brand" },
  { key: "category", label: "Category" },
  { key: "cost_price", label: "Cost price" },
  { key: "barcode", label: "Barcode / EAN / UPC" },
  { key: "minimum_stock", label: "Minimum stock" },
  { key: "location", label: "Location / bin" },
];

const EMPTY_MAPPING = {
  external_id: "",
  name: "",
  price: "",
  quantity: "",
  brand: "",
  category: "",
  cost_price: "",
  barcode: "",
  minimum_stock: "",
  location: "",
};

function extensionOf(filename = "") {
  const lower = filename.toLowerCase();
  return ACCEPTED_EXTENSIONS.find((extension) => lower.endsWith(extension)) || "";
}

function bytesLabel(bytes) {
  if (!Number.isFinite(Number(bytes))) return "—";
  const value = Number(bytes);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function summaryCards(summary) {
  if (!summary) return [];
  return [
    ["Rows", summary.total_rows],
    ["Valid", summary.valid_rows],
    ["Invalid", summary.invalid_rows],
    ["Products to create", summary.products_to_create],
    ["Products to update", summary.products_to_update],
    ["Inventory to create", summary.inventory_to_create],
    ["Inventory to update", summary.inventory_to_update],
    ["Barcodes to create", summary.barcodes_to_create],
    ["Barcodes already present", summary.barcodes_already_present],
  ];
}

function MappingSelect({ field, value, columns, onChange, required = false }) {
  return (
    <Box>
      <FormControl fullWidth required={required}>
        <InputLabel>{field.label}</InputLabel>
        <Select
          label={field.label}
          value={value}
          onChange={(event) => onChange(field.key, event.target.value)}
        >
          <MenuItem value=""><em>Not mapped</em></MenuItem>
          {columns.map((column) => (
            <MenuItem key={column.key} value={column.key}>
              {column.key} — {column.header || "Untitled column"}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {field.helper && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.7 }}>
          {field.helper}
        </Typography>
      )}
    </Box>
  );
}

function IssuesTable({ title, rows, severity }) {
  if (!rows?.length) return null;
  return (
    <Card sx={{ borderRadius: 3.5, border: "1px solid #e2e8f0", boxShadow: "none" }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ px: 2.25, py: 1.8 }}>
          <Typography fontWeight={900}>{title}</Typography>
        </Box>
        <Divider />
        <TableContainer sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Row</TableCell>
                <TableCell>Field</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((issue, index) => (
                <TableRow key={`${issue.row_number}-${issue.code}-${index}`}>
                  <TableCell>{issue.row_number}</TableCell>
                  <TableCell>{issue.field || "—"}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color={severity === "error" ? "error.main" : "warning.dark"}>
                      {issue.message}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default function StoreImport() {
  const navigate = useNavigate();
  const { selectedStore, selectedWorkspace } = useAuth();
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [headerRow, setHeaderRow] = useState(1);
  const [inspection, setInspection] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [mapping, setMapping] = useState(EMPTY_MAPPING);
  const [preview, setPreview] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const activeSheet = useMemo(
    () => inspection?.sheets?.find((sheet) => sheet.name === sheetName) || inspection?.sheets?.[0] || null,
    [inspection, sheetName],
  );

  const columns = activeSheet?.columns || [];
  const requiredMapped = REQUIRED_FIELDS.every((field) => Boolean(mapping[field.key]));
  const activeStep = commitResult ? 3 : preview ? 2 : inspection ? 1 : 0;

  const config = useMemo(() => ({
    sheet_name: sheetName || null,
    header_row: Number(headerRow || 1),
    mapping: Object.fromEntries(
      Object.entries(mapping).map(([key, value]) => [key, value || null]),
    ),
  }), [headerRow, mapping, sheetName]);

  const reset = () => {
    setFile(null);
    setHeaderRow(1);
    setInspection(null);
    setSheetName("");
    setMapping(EMPTY_MAPPING);
    setPreview(null);
    setCommitResult(null);
    setBusy("");
    setError("");
    setConfirmOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    reset();
    // A migration file/config must never follow a workspace/store switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspace?.public_id, selectedStore?.public_id]);

  const chooseFile = (nextFile) => {
    setError("");
    setInspection(null);
    setSheetName("");
    setMapping(EMPTY_MAPPING);
    setPreview(null);
    setCommitResult(null);

    if (!nextFile) {
      setFile(null);
      return;
    }
    if (!extensionOf(nextFile.name)) {
      setFile(null);
      setError("Choose an .xlsx or .csv export from the old POS.");
      return;
    }
    if (nextFile.size > MAX_FILE_BYTES) {
      setFile(null);
      setError("The import file is larger than the 25 MB server limit.");
      return;
    }
    setFile(nextFile);
  };

  const inspect = async () => {
    if (!file || busy) return;
    setBusy("inspect");
    setError("");
    setPreview(null);
    setCommitResult(null);
    try {
      const result = await inspectCatalogSpreadsheet(file, Number(headerRow || 1));
      setInspection(result);
      setSheetName(result.sheets?.[0]?.name || "");
      setMapping(EMPTY_MAPPING);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not inspect the spreadsheet."));
    } finally {
      setBusy("");
    }
  };

  const updateHeaderRow = (value) => {
    const normalized = Math.max(1, Math.min(100, Number(value || 1)));
    setHeaderRow(normalized);
    setInspection(null);
    setSheetName("");
    setMapping(EMPTY_MAPPING);
    setPreview(null);
    setCommitResult(null);
  };

  const updateMapping = (key, value) => {
    setMapping((current) => ({ ...current, [key]: value }));
    setPreview(null);
    setCommitResult(null);
  };

  const runPreview = async () => {
    if (!file || !inspection || !requiredMapped || busy) return;
    setBusy("preview");
    setError("");
    try {
      const result = await previewCatalogSpreadsheet(file, config);
      setPreview(result);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not preview the import."));
    } finally {
      setBusy("");
    }
  };

  const commit = async () => {
    if (!file || !preview?.safe_to_commit || busy) return;
    setConfirmOpen(false);
    setBusy("commit");
    setError("");
    try {
      const result = await commitCatalogSpreadsheet(file, config);
      setCommitResult(result);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not commit the store import."));
    } finally {
      setBusy("");
    }
  };

  return (
    <MainLayout>
      <PageHeader
        eyebrow="Store migration"
        title="Import store data"
        description="Move the old POS catalog into ABOS once, validate it before writing anything, then use ABOS Native POS as the store source of truth."
        status={{ label: selectedStore?.name || "Current store", color: "primary", variant: "outlined" }}
        actions={file && (
          <Button variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={reset} disabled={Boolean(busy)}>
            Start over
          </Button>
        )}
      />

      <Alert severity="info" sx={{ mb: 2.5, borderRadius: 3 }}>
        Export your products and stock from the old POS as Excel (.xlsx) or CSV. Preview is read-only. Opening inventory can only be committed before this store starts Native POS/order operations.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 3 }}>{error}</Alert>}

      <Card sx={{ borderRadius: 4, mb: 2.5 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ overflowX: "auto", pb: 0.5 }}>
            {STEPS.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {!inspection && !commitResult && (
        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: { xs: 2.25, md: 3.25 } }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6">1. Upload the old POS export</Typography>
                <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
                  Maximum 25 MB. Up to 100,000 data rows are accepted by the importer.
                </Typography>
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2.5, sm: 4 },
                  borderRadius: 4,
                  borderStyle: "dashed",
                  textAlign: "center",
                  bgcolor: "#f8fafc",
                }}
              >
                <FileUploadRoundedIcon sx={{ fontSize: 42, color: "primary.main" }} />
                <Typography fontWeight={900} sx={{ mt: 1 }}>Excel or CSV</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Select the file exported from the previous POS.
                </Typography>
                <Button component="label" variant="contained" sx={{ mt: 2 }}>
                  Choose file
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                    onChange={(event) => chooseFile(event.target.files?.[0] || null)}
                  />
                </Button>
                {file && (
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="center" spacing={1} sx={{ mt: 2 }}>
                    <Chip icon={<TableChartRoundedIcon />} label={file.name} />
                    <Chip label={bytesLabel(file.size)} variant="outlined" />
                  </Stack>
                )}
              </Paper>

              <TextField
                label="Header row"
                type="number"
                value={headerRow}
                onChange={(event) => updateHeaderRow(event.target.value)}
                inputProps={{ min: 1, max: 100 }}
                helperText="Usually 1. Change this only if the spreadsheet has title rows above the column names."
                sx={{ maxWidth: 360 }}
              />

              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={inspect}
                  disabled={!file || Boolean(busy)}
                  startIcon={busy === "inspect" ? <CircularProgress size={18} /> : <TableChartRoundedIcon />}
                >
                  Inspect file
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {inspection && !preview && !commitResult && (
        <Stack spacing={2.5}>
          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: { xs: 2.25, md: 3.25 } }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
                <Box>
                  <Typography variant="h6">2. Map the spreadsheet columns</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    ABOS does not assume vendor-specific column names. Choose exactly what each column means.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={inspection.filename} />
                  <Chip label={`${inspection.file_type?.toUpperCase()} · ${bytesLabel(inspection.file_size)}`} variant="outlined" />
                </Stack>
              </Stack>

              <Grid container spacing={2.25} sx={{ mt: 0.5 }}>
                {inspection.sheets?.length > 1 && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Worksheet</InputLabel>
                      <Select
                        value={sheetName}
                        label="Worksheet"
                        onChange={(event) => {
                          setSheetName(event.target.value);
                          setMapping(EMPTY_MAPPING);
                        }}
                      >
                        {inspection.sheets.map((sheet) => (
                          <MenuItem key={sheet.name} value={sheet.name}>{sheet.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography fontWeight={900} sx={{ mb: 1.5 }}>Required fields</Typography>
              <Grid container spacing={2.25}>
                {REQUIRED_FIELDS.map((field) => (
                  <Grid item xs={12} sm={6} key={field.key}>
                    <MappingSelect
                      field={field}
                      value={mapping[field.key]}
                      columns={columns}
                      onChange={updateMapping}
                      required
                    />
                  </Grid>
                ))}
              </Grid>

              <Typography fontWeight={900} sx={{ mt: 3, mb: 1.5 }}>Optional fields</Typography>
              <Grid container spacing={2.25}>
                {OPTIONAL_FIELDS.map((field) => (
                  <Grid item xs={12} sm={6} md={4} key={field.key}>
                    <MappingSelect
                      field={field}
                      value={mapping[field.key]}
                      columns={columns}
                      onChange={updateMapping}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: { xs: 2.25, md: 3.25 } }}>
              <Typography variant="h6">Spreadsheet sample</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                Up to five rows are shown only to help verify the mapping.
              </Typography>
              <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
                <Table size="small" sx={{ minWidth: Math.max(700, columns.length * 140) }}>
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell key={column.key}>
                          <Typography variant="caption" color="text.secondary" display="block">{column.key}</Typography>
                          <Typography variant="body2" fontWeight={900}>{column.header || "Untitled"}</Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(activeSheet?.sample_rows || []).map((row, index) => (
                      <TableRow key={index}>
                        {columns.map((column) => (
                          <TableCell key={column.key} sx={{ maxWidth: 240, overflowWrap: "anywhere" }}>
                            {row[column.key] === null || row[column.key] === undefined || row[column.key] === ""
                              ? <Typography color="text.secondary">—</Typography>
                              : String(row[column.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {!requiredMapped && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Map External ID / SKU, Product name, Selling price, and Opening quantity before previewing.
                </Alert>
              )}

              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1.5} sx={{ mt: 2.5 }}>
                <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => setInspection(null)} disabled={Boolean(busy)}>
                  Back to upload
                </Button>
                <Button
                  variant="contained"
                  onClick={runPreview}
                  disabled={!requiredMapped || Boolean(busy)}
                  startIcon={busy === "preview" ? <CircularProgress size={18} /> : <Inventory2RoundedIcon />}
                >
                  Preview import
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}

      {preview && !commitResult && (
        <Stack spacing={2.5}>
          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: { xs: 2.25, md: 3.25 } }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
                <Box>
                  <Typography variant="h6">3. Review before import</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    This preview was calculated without writing products, inventory, or barcodes.
                  </Typography>
                </Box>
                <Chip
                  icon={preview.safe_to_commit ? <CheckCircleRoundedIcon /> : <WarningAmberRoundedIcon />}
                  label={preview.safe_to_commit ? "Safe to import" : "Import blocked"}
                  color={preview.safe_to_commit ? "success" : "warning"}
                />
              </Stack>

              <Grid container spacing={1.5} sx={{ mt: 1 }}>
                {summaryCards(preview.summary).map(([label, value]) => (
                  <Grid item xs={6} sm={4} lg={3} key={label}>
                    <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3, height: "100%" }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={800}>{label}</Typography>
                      <Typography variant="h6" sx={{ mt: 0.4 }}>{Number(value || 0).toLocaleString()}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {preview.blockers?.length > 0 && (
                <Alert severity="warning" sx={{ mt: 2.5 }}>
                  <Typography fontWeight={900} sx={{ mb: 0.5 }}>Opening import is blocked for this store</Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.4}>
                    {preview.blockers.map((blocker) => <li key={blocker}>{blocker}</li>)}
                  </Stack>
                </Alert>
              )}
            </CardContent>
          </Card>

          <IssuesTable title={`Errors (${preview.errors?.length || 0})`} rows={preview.errors} severity="error" />
          <IssuesTable title={`Warnings (${preview.warnings?.length || 0})`} rows={preview.warnings} severity="warning" />

          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: { xs: 2.25, md: 3 } }}>
              <Alert severity={preview.safe_to_commit ? "info" : "warning"}>
                {preview.safe_to_commit
                  ? "Import creates/updates canonical ABOS products, sets this store's opening physical inventory, and assigns valid barcodes. It does not import historical sales or deduct stock."
                  : "Resolve the blockers/errors before attempting the opening import. No database changes have been made."}
              </Alert>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1.5} sx={{ mt: 2.5 }}>
                <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => setPreview(null)} disabled={Boolean(busy)}>
                  Change mapping
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  disabled={!preview.safe_to_commit || Boolean(busy)}
                  onClick={() => setConfirmOpen(true)}
                  startIcon={busy === "commit" ? <CircularProgress size={18} /> : <CheckCircleRoundedIcon />}
                >
                  Import opening catalog
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}

      {commitResult && (
        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Stack alignItems="center" textAlign="center" spacing={1.5}>
              <Box sx={{ width: 66, height: 66, borderRadius: "50%", bgcolor: "#dcfce7", color: "#15803d", display: "grid", placeItems: "center" }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 38 }} />
              </Box>
              <Typography variant="h5">Opening catalog imported</Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 720 }}>
                ABOS now owns the imported catalog and opening stock for {selectedStore?.name || "this store"}. Do not keep the old POS synchronizing inventory after cutover.
              </Typography>
            </Stack>

            <Grid container spacing={1.5} sx={{ mt: 2.5 }}>
              {summaryCards(commitResult.summary).map(([label, value]) => (
                <Grid item xs={6} sm={4} lg={3} key={label}>
                  <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 3, height: "100%" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={800}>{label}</Typography>
                    <Typography variant="h6" sx={{ mt: 0.4 }}>{Number(value || 0).toLocaleString()}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="center" gap={1.25}>
              <Button variant="outlined" onClick={() => navigate("/products")}>Review products</Button>
              <Button variant="outlined" onClick={() => navigate("/inventory")}>Review inventory</Button>
              <Button variant="contained" onClick={() => navigate("/pos")}>Open Point of Sale</Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={900}>Commit opening store data?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is an opening-stock migration. Once the store begins Native POS or order operations, ABOS blocks this import path from overwriting operational stock.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            You are importing {Number(preview?.summary?.valid_rows || 0).toLocaleString()} valid row(s) into <strong>{selectedStore?.name || "the selected store"}</strong>. Verify the preview counts before continuing.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy === "commit"}>Cancel</Button>
          <Button variant="contained" color="success" onClick={commit} disabled={busy === "commit"}>
            {busy === "commit" ? "Importing…" : "Confirm import"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
}
